"use client";

import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/overlays/popover";
import { useNumberRulesInput } from "@/hooks/useNumberRulesInput";
import { getNumberRuleValuePlaceholder } from "@/components/ui/utils/number-rules-input-utils";

// =============================================================================
// Types
// =============================================================================

export interface NumberRule {
  operator: "gte" | "lte";
  value: string;
}

// =============================================================================
// Number Rules Input (for Net Weight / Gross Weight: up to 2 rules, gte/lte + value)
// =============================================================================

export function NumberRulesInput({
  columnLabel,
  columnKey,
  rules,
  onRulesChange,
  onApply,
}: {
  columnLabel: string;
  /** When "netWeight" or "grossWeight", uses dedicated label for the trigger placeholder */
  columnKey?: string;
  rules: NumberRule[];
  onRulesChange: (rules: NumberRule[]) => void;
  onApply: () => void;
}) {
  const { t } = useTranslation("common", { keyPrefix: "numberRules" });
  const {
    open,
    applyError,
    rulesToShow,
    canAddRule,
    hasAtLeastOneValue,
    summary,
    addRule,
    updateRule,
    removeRule,
    handleOpenChange,
    handleClear,
    handleApply,
  } = useNumberRulesInput({
    rules,
    onRulesChange,
    onApply,
    t,
    columnKey,
    columnLabel,
  });

  const valuePlaceholder = getNumberRuleValuePlaceholder(columnKey, t);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex-1 min-w-0 -ml-px flex items-center gap-2",
            "border border-border-secondary rounded-none",
            "px-[11.5px] py-[8px]",
            "bg-bg-primary text-left body-sm-regular",
            "shadow-[0px_1px_2px_0px_rgba(18,18,23,0.05)]",
            "text-text-tertiary hover:text-text-secondary",
            "outline-none transition-colors"
          )}
        >
          <span className="flex-1 min-w-0 truncate">{summary}</span>
          <i className="pi pi-chevron-down text-xs shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3 bg-bg-primary border border-border-secondary shadow-md" align="end">
        <div className="space-y-3">
          {rulesToShow.map((rule, index) => (
            <div key={index} className="space-y-2">
              <select
                value={rule.operator}
                onChange={(e) =>
                  updateRule(index, { operator: e.target.value as NumberRule["operator"] })
                }
                className={cn(
                  "w-full px-2 py-1.5 text-sm rounded-sm",
                  "bg-bg-primary border border-border-secondary",
                  "outline-none focus:border-(--input-border-focus)"
                )}
              >
                <option value="gte">{t("greaterThan")}</option>
                <option value="lte">{t("lessThan")}</option>
              </select>
              <input
                type="number"
                inputMode="decimal"
                value={rule.value}
                onChange={(e) => updateRule(index, { value: e.target.value })}
                placeholder={valuePlaceholder}
                className={cn(
                  "w-full px-2 py-1.5 text-sm rounded-sm",
                  "bg-bg-primary border border-border-secondary",
                  "outline-none focus:border-(--input-border-focus)"
                )}
              />
              {rules.length >= 2 && index < rules.length && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeRule(index)}
                    className="flex items-center gap-1.5 px-0 py-1 text-sm text-error-600 hover:bg-error-50 rounded-sm transition-colors"
                  >
                    <i className="pi pi-trash text-xs" aria-hidden />
                    {t("removeRule")}
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 mt-1">
            {canAddRule ? (
              <button
                type="button"
                onClick={addRule}
                className="text-sm text-brand-600 hover:text-brand-700 font-medium"
              >
                {t("addRule")}
              </button>
            ) : (
              <span />
            )}
          </div>
          {applyError && (
            <p className="text-sm text-error-600 mt-1 mb-0.5" role="alert">
              {t(applyError)}
            </p>
          )}
          <div className="flex gap-2 pt-2 mt-2">
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-sm border border-border-secondary text-text-secondary hover:bg-bg-hover transition-colors"
            >
              {t("clear")}
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!hasAtLeastOneValue}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-sm bg-brand-600 text-white transition-colors",
                hasAtLeastOneValue
                  ? "hover:bg-brand-700"
                  : "opacity-40 cursor-not-allowed pointer-events-none"
              )}
            >
              {t("apply")}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
